import type { InventoryEntry, SavedDeck, SavedDeckEntry } from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

export type MissingCard = {
  cardId: string;
  card: CardRecord;
  needed: number;
  owned: number;
  missing: number;
  sources: Array<"main" | "sideboard" | "legend" | "champion">;
};

function bumpNeed(
  map: Map<string, { needed: number; sources: Set<"main" | "sideboard" | "legend" | "champion"> }>,
  cardId: string,
  quantity: number,
  source: "main" | "sideboard" | "legend" | "champion",
) {
  if (!cardId || quantity <= 0) return;
  const existing = map.get(cardId) ?? { needed: 0, sources: new Set() };
  existing.needed += quantity;
  existing.sources.add(source);
  map.set(cardId, existing);
}

/** Agrège les besoins d'un deck (main + sideboard + légende + champion). */
export function deckCardNeeds(deck: SavedDeck): Map<string, number> {
  const needs = new Map<string, number>();
  const add = (entries: SavedDeckEntry[]) => {
    for (const e of entries) {
      if (e.quantity <= 0) continue;
      needs.set(e.cardId, (needs.get(e.cardId) ?? 0) + e.quantity);
    }
  };
  add(deck.entries);
  add(deck.sideboard ?? []);
  if (deck.legendId) {
    needs.set(deck.legendId, (needs.get(deck.legendId) ?? 0) + 1);
  }
  if (deck.championId) {
    // champion copies are already in entries most of the time; don't double-count if present
    if (!needs.has(deck.championId)) {
      needs.set(deck.championId, 1);
    }
  }
  for (const bf of deck.battlefieldIds ?? []) {
    needs.set(bf, (needs.get(bf) ?? 0) + 1);
  }
  return needs;
}

export function findMissingCards(
  deck: SavedDeck,
  inventory: InventoryEntry[],
  cards: CardRecord[],
): MissingCard[] {
  const qtyMap = new Map(inventory.map((e) => [e.cardId, e.quantity]));
  const byId = new Map(cards.map((c) => [c.id, c]));
  const detailed = new Map<
    string,
    { needed: number; sources: Set<"main" | "sideboard" | "legend" | "champion"> }
  >();

  for (const e of deck.entries) bumpNeed(detailed, e.cardId, e.quantity, "main");
  for (const e of deck.sideboard ?? []) bumpNeed(detailed, e.cardId, e.quantity, "sideboard");
  if (deck.legendId) bumpNeed(detailed, deck.legendId, 1, "legend");
  if (deck.championId && !(deck.entries ?? []).some((e) => e.cardId === deck.championId)) {
    bumpNeed(detailed, deck.championId, 1, "champion");
  }
  for (const bf of deck.battlefieldIds ?? []) bumpNeed(detailed, bf, 1, "main");

  const missing: MissingCard[] = [];
  for (const [cardId, info] of detailed) {
    const owned = qtyMap.get(cardId) ?? 0;
    const gap = info.needed - owned;
    if (gap <= 0) continue;
    const card = byId.get(cardId);
    if (!card) continue;
    missing.push({
      cardId,
      card,
      needed: info.needed,
      owned,
      missing: gap,
      sources: [...info.sources],
    });
  }

  return missing.sort((a, b) => b.missing - a.missing || a.card.name.localeCompare(b.card.name));
}

export function missingCardsSummary(missing: MissingCard[]) {
  const unique = missing.length;
  const copies = missing.reduce((s, m) => s + m.missing, 0);
  return { unique, copies };
}
